import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useClickOutside } from "@Shared/Hooks/useClickOutside";

import SecurityIcon from "./icons/Security";
import SupportIcon from "./icons/Support";
import GuidesIcon from "./icons/Guides";
import DemoIcon from "./icons/Demo";
import LinkIcon from "./icons/Link";

const Container = styled.div`
  position: fixed;
  margin-left: 180px;
  bottom: 70px;
  border-radius: 20px;
  padding: 16px;
  background: ${({ theme }) => theme.bg.general};
  border: 1px solid ${({ theme }) => theme.border.black10};
  box-shadow: 0 4px 12px 0
    ${({ theme }) => (theme.name === "dark" ? "#000000" : "#00296f")}40;

  svg {
    color: ${({ theme }) => theme.text.brand};
  }
`;

const Title = styled.div`
  font-family: "PP Formula";
  font-size: 13px;
  color: ${({ theme }) => theme.text.title};
`;

const Description = styled.div`
  font-size: 11px;
  padding-bottom: 15px;
  margin: 6px 0 15px;
  border-bottom: 1px solid ${({ theme }) => theme.border.black10};
  color: ${({ theme }) => theme.text.text};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  font-size: 12px;
  font-weight: 500;
  border-bottom: 1px solid ${({ theme }) => theme.border.black10};
  padding-bottom: 15px;
  margin-bottom: 15px;
`;

const Link = styled.a`
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 10px;
  color: ${({ theme }) => theme.text.title};
  text-decoration: none;

  & > span {
    flex: 1;
  }

  .link-icon {
    color: ${({ theme }) => theme.border.black50};
  }
`;

const HelpCenter = ({ isHelpCenterOpen, setIsHelpCenterOpen }) => {
  const helpCenterRef = useRef(null);

  const { t } = useTranslation();
  const prefix = "help-center.";

  useClickOutside(helpCenterRef, () => setIsHelpCenterOpen(false));

  if (!isHelpCenterOpen) return null;

  return (
    <Container ref={helpCenterRef}>
      <Title>{t(prefix + "help-center")}</Title>
      <Description>{t(prefix + "need-help")}</Description>
      <Content>
        <Link
          href="https://caelor.atlassian.net/servicedesk/customer/portal/1/group/1"
          target="_blank"
        >
          <SupportIcon />
          <span>{t(prefix + "support-center")}</span>
          <LinkIcon />
        </Link>

        <Link href="https://calendly.com/caelor/demo" target="_blank">
          <DemoIcon />
          <span>{t(prefix + "book-demo")}</span>
          <LinkIcon />
        </Link>

        <Link
          href="https://caelor-apps.atlassian.net/wiki/spaces/OCFM/overview?homepageId=7799179"
          target="_blank"
        >
          <GuidesIcon />
          <span>{t(prefix + "guides")}</span>
          <LinkIcon />
        </Link>

        <Link
          href="https://caelor-apps.atlassian.net/wiki/spaces/Caelor/pages/10027091/Security+and+Data+Hosting"
          target="_blank"
        >
          <SecurityIcon />
          <span>{t(prefix + "security")}</span>
          <LinkIcon />
        </Link>
      </Content>
    </Container>
  );
};

export default HelpCenter;
